import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Checkbox } from '../../components/ui/Checkbox';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { useDocuments } from '../../core/hooks/useDocuments';
import { useClients } from '../../core/hooks/useClients';
import { useOrganizations } from '../../core/hooks/useOrganizations';
import { useAuth } from '../../core/contexts/AuthContext';
import { useCurrentOrganization } from '../../core/hooks/useCurrentOrganization';
import { collectionGroup, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../core/services/firebase';
import type { Document, DocumentTemplateField } from '../../core/models/Document';
import { processTemplate } from '../../core/utils/templateLoader';
import { findClientAccountForProject, resolveProjectContactEmail } from '../../core/utils/projectContact';
import { UserRole } from '../../core/models/User';
import { logError } from '../../lib/utils';

export const DocumentEditPage: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const { currentUser, organizationId } = useAuth();
  const { updateDocument } = useDocuments(null, organizationId);
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
  const effectiveOrgIdForClients = isSuperAdmin ? null : organizationId;
  const { clients, loading: clientsLoading } = useClients(effectiveOrgIdForClients);
  const { organizations } = useOrganizations();
  const { organization } = useCurrentOrganization(organizationId);
  const [document, setDocument] = useState<Document | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [dynamicFields, setDynamicFields] = useState<Record<string, any>>({});
  const [logoSource, setLogoSource] = useState<'none' | 'organization' | 'client' | 'both'>('none');

  // Load document
  useEffect(() => {
    const fetchDocument = async () => {
      if (!documentId) {
        setError(new Error('Document ID is required'));
        setLoading(false);
        return;
      }

      try {
        let q;
        if (organizationId && currentUser?.role !== UserRole.SUPER_ADMIN) {
          q = query(
            collectionGroup(db, 'documents'),
            where('organizationId', '==', organizationId)
          );
        } else {
          q = query(collectionGroup(db, 'documents'));
        }

        const querySnapshot = await getDocs(q);
        const foundDoc = querySnapshot.docs.find(doc => doc.id === documentId);

        if (foundDoc) {
          const data = foundDoc.data();
          const doc = {
            id: foundDoc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || data.metadata?.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || data.metadata?.updatedAt?.toDate() || new Date(),
          } as Document;

          setDocument(doc);
          setTitle(doc.title);
          setSelectedClientId(doc.clientId);
          setDynamicFields(doc.dynamicFields || {});
          setLogoSource(doc.logoSource || 'none');
        } else {
          setError(new Error('Document not found'));
        }
      } catch (err) {
        logError('documents', err, { source: 'DocumentEditPage.fetchDocument', documentId });
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [documentId, organizationId, currentUser?.role]);

  // Get template fields from document
  const templateFields = document?.templateType?.dynamicFields || [];

  // Auto-populate client-related fields when client changes
  useEffect(() => {
    if (!selectedClientId || !document) return;

    const selectedClient = clients.find((c) => c.id === selectedClientId);
    if (!selectedClient) return;
    const clientAccount = findClientAccountForProject(selectedClient, organizations);
    const contactEmail = resolveProjectContactEmail(selectedClient, clientAccount);

    const updatedFields = { ...dynamicFields };
    templateFields.forEach((field) => {
      if (field.type === 'client') {
        if (field.key === 'client_name') {
          updatedFields[field.key] = selectedClient.name;
        }
        if (field.key === 'client_email') {
          updatedFields[field.key] = contactEmail;
        }
      }
    });
    setDynamicFields(updatedFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dynamicFields/templateFields omitted to avoid resetting user edits on unrelated updates
  }, [selectedClientId, clients, organizations, document]);

  // Render a single field
  const renderField = (field: DocumentTemplateField) => {
    const fieldValue = dynamicFields[field.key] || '';

    switch (field.type) {
      case 'text':
        return (
          <Input
            key={field.key}
            label={field.label}
            value={fieldValue}
            onChange={(e) => setDynamicFields({ ...dynamicFields, [field.key]: e.target.value })}
            placeholder={field.placeholder}
            required={field.required}
          />
        );

      case 'number':
        return (
          <Input
            key={field.key}
            label={field.label}
            type="number"
            value={fieldValue}
            onChange={(e) => setDynamicFields({ ...dynamicFields, [field.key]: e.target.value })}
            placeholder={field.placeholder}
            required={field.required}
          />
        );

      case 'date':
        return (
          <Input
            key={field.key}
            label={field.label}
            type="date"
            value={fieldValue}
            onChange={(e) => setDynamicFields({ ...dynamicFields, [field.key]: e.target.value })}
            required={field.required}
          />
        );

      case 'boolean':
        return (
          <div key={field.key} className="flex items-center space-x-2">
            <Checkbox
              checked={fieldValue === true}
              onCheckedChange={(checked) => setDynamicFields({ ...dynamicFields, [field.key]: checked === true })}
            />
            <label className="text-sm font-medium">{field.label}</label>
          </div>
        );

      case 'select':
        return (
          <div key={field.key} className="space-y-2">
            <label className="text-sm font-medium">
              {field.label}
              {field.required && <span className="text-error ml-1">*</span>}
            </label>
            <Select
              value={fieldValue || ''}
              onValueChange={(value) => setDynamicFields({ ...dynamicFields, [field.key]: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={field.placeholder || 'Select an option'} />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'multiline':
        return (
          <Textarea
            key={field.key}
            label={field.label}
            value={fieldValue}
            onChange={(e) => setDynamicFields({ ...dynamicFields, [field.key]: e.target.value })}
            placeholder={field.placeholder}
            rows={4}
            required={field.required}
          />
        );

      case 'client': {
        const selectedClient = clients.find((c) => c.id === selectedClientId);
        const clientAccount = findClientAccountForProject(selectedClient, organizations);
        const clientFieldValue = selectedClient
          ? field.key === 'client_name'
            ? selectedClient.name
            : field.key === 'client_email'
            ? resolveProjectContactEmail(selectedClient, clientAccount)
            : fieldValue
          : fieldValue;

        return (
          <Input
            key={field.key}
            label={field.label}
            value={clientFieldValue}
            disabled={!!selectedClient}
            placeholder={field.placeholder}
          />
        );
      }

      case 'services':
        return (
          <div key={field.key} className="space-y-2">
            <label className="text-sm font-medium">{field.label}</label>
            <Textarea
              value={fieldValue}
              onChange={(e) => setDynamicFields({ ...dynamicFields, [field.key]: e.target.value })}
              placeholder="Enter services (one per line)"
              rows={3}
            />
          </div>
        );

      default:
        return null;
    }
  };

  // Process template content with updated fields
  const processedContent = useMemo(() => {
    // For markdown documents (templateType is null), just return the existing content
    if (!document?.templateType?.content) {
      return document?.content || '';
    }

    const selectedClient = selectedClientId ? clients.find((c) => c.id === selectedClientId) : undefined;
    const clientAccount = findClientAccountForProject(selectedClient, organizations);
    const orgLogoUrl = organization?.branding?.logoUrl || '';
    const clientLogoUrl = selectedClient?.logoUrl || '';

    const showOrgLogo = (logoSource === 'organization' || logoSource === 'both') && !!orgLogoUrl;
    const showClientLogo = (logoSource === 'client' || logoSource === 'both') && !!clientLogoUrl;

    const allDynamicFields: Record<string, any> = {
      ...dynamicFields,
      org_name: organization?.name || '',
      org_email: organization?.email || '',
      client_name: selectedClient?.name || '',
      client_email: resolveProjectContactEmail(selectedClient, clientAccount),
      org_logo_url: showOrgLogo ? orgLogoUrl : '',
      client_logo_url: showClientLogo ? clientLogoUrl : '',
      show_org_logo: showOrgLogo,
      show_client_logo: showClientLogo,
    };

    return processTemplate(
      document.templateType.content,
      allDynamicFields,
      title,
      selectedClient?.name,
      resolveProjectContactEmail(selectedClient, clientAccount)
    );
  }, [document, dynamicFields, title, selectedClientId, clients, organizations, organization, logoSource]);

  const handleSave = async () => {
    if (!document || !currentUser) return;

    if (!title.trim()) {
      alert('Please enter a document title.');
      return;
    }

    if (!selectedClientId) {
      alert('Please select a project.');
      return;
    }

    setSaving(true);
    try {
      const selectedClient = clients.find((c) => c.id === selectedClientId);
      const clientAccount = findClientAccountForProject(selectedClient, organizations);
      const contactEmail = resolveProjectContactEmail(selectedClient, clientAccount);
      const orgLogoUrl = organization?.branding?.logoUrl || '';
      const clientLogoUrl = selectedClient?.logoUrl || '';

      const showOrgLogo = (logoSource === 'organization' || logoSource === 'both') && !!orgLogoUrl;
      const showClientLogo = (logoSource === 'client' || logoSource === 'both') && !!clientLogoUrl;

      const allDynamicFields: Record<string, any> = {
        ...dynamicFields,
        org_name: organization?.name || '',
        org_email: organization?.email || '',
        client_name: selectedClient?.name || '',
        client_email: contactEmail,
        org_logo_url: showOrgLogo ? orgLogoUrl : '',
        client_logo_url: showClientLogo ? clientLogoUrl : '',
        show_org_logo: showOrgLogo,
        show_client_logo: showClientLogo,
      };

      // For markdown documents (no templateType), don't reprocess content
      const updates: Partial<Document> = {
        title: title.trim(),
        clientId: selectedClientId,
        dynamicFields: allDynamicFields,
        logoSource,
        orgLogoUrl: showOrgLogo ? orgLogoUrl : '',
        clientLogoUrl: showClientLogo ? clientLogoUrl : '',
      };

      // Only update content if we have a template to reprocess
      if (document.templateType?.content) {
        updates.content = processedContent;
      }

      // If clientId changed, we need to move the document
      // The updateDocument function will handle this
      await updateDocument(document.id, updates);

      navigate(`/documents/${document.id}`);
    } catch (err: any) {
      logError('documents', err, { source: 'DocumentEditPage.handleSave', documentId: document.id });
      alert(err?.message || 'Failed to update document. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || clientsLoading) {
    return <LoadingState message="Loading document..." />;
  }

  if (error || !document) {
    return <ErrorState message={error?.message || 'Document not found'} />;
  }

  const selectedClient = clients.find((c) => c.id === selectedClientId);
  const orgLogoUrl = organization?.branding?.logoUrl || '';
  const clientLogoUrl = selectedClient?.logoUrl || '';

  return (
    <div className="space-y-6 w-full max-w-full overflow-x-hidden" style={{ minHeight: 0 }}>
      <PageHeader
        title="Edit Document"
        description="Update document information, project association, and fields"
        icon={FileText}
        actions={
          <Button variant="outline" onClick={() => navigate(`/documents/${document.id}`)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Cancel
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
        {/* Left Column - Form */}
        <div className="space-y-6 min-w-0">
          <Card>
            <CardHeader>
              <CardTitle>Document Information</CardTitle>
              <CardDescription>Update the document title and project association</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                label="Document Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Service Agreement - Acme Corp"
                required
              />

              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Project <span className="text-error">*</span>
                </label>
                {clients.length === 0 ? (
                  <p className="text-sm text-gray-500">No projects available.</p>
                ) : (
                  <Select
                    value={selectedClientId}
                    onValueChange={setSelectedClientId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a project" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="border-t pt-4 space-y-3">
                <h3 className="font-semibold">Logo Options</h3>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={logoSource === 'organization' || logoSource === 'both'}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setLogoSource(logoSource === 'client' ? 'both' : 'organization');
                        } else {
                          setLogoSource(logoSource === 'both' ? 'client' : 'none');
                        }
                      }}
                      disabled={!orgLogoUrl}
                    />
                    <span className="text-sm">
                      Use client logo
                      {!orgLogoUrl && (
                        <span className="ml-2 text-xs text-muted-foreground">(no logo uploaded)</span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={logoSource === 'client' || logoSource === 'both'}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setLogoSource(logoSource === 'organization' ? 'both' : 'client');
                        } else {
                          setLogoSource(logoSource === 'both' ? 'organization' : 'none');
                        }
                      }}
                      disabled={!selectedClientId || !clientLogoUrl}
                    />
                    <span className="text-sm">
                      Use project logo
                      {!selectedClientId && (
                        <span className="ml-2 text-xs text-muted-foreground">(select a project first)</span>
                      )}
                      {selectedClientId && !clientLogoUrl && (
                        <span className="ml-2 text-xs text-muted-foreground">(no logo uploaded)</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {templateFields.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Template Fields</CardTitle>
                <CardDescription>Update the dynamic fields for this document</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {templateFields.map((field) => (
                  <div key={field.key}>
                    {renderField(field)}
                    {field.helpText && (
                      <p className="text-xs text-gray-500 mt-1">{String(field.helpText)}</p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Preview */}
        <div className="space-y-6 min-w-0">
          <Card>
            <CardHeader>
              <CardTitle>Preview</CardTitle>
              <CardDescription>Preview of the document with current values</CardDescription>
            </CardHeader>
            <CardContent>
              <iframe
                srcDoc={processedContent}
                className="w-full rounded-lg border bg-white"
                style={{ height: '800px' }}
                title="Document Preview"
                sandbox="allow-same-origin"
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button
          variant="outline"
          onClick={() => navigate(`/documents/${document.id}`)}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          loading={saving}
          disabled={saving || !title.trim() || !selectedClientId}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};
